import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Eye,
  EyeOff,
  Send,
  Plus,
  CreditCard,
  Shield,
  RefreshCw,
  AlertCircle,
  Copy,
  Check,
  TrendingUp,
  TrendingDown,
  Wallet,
  BarChart3,
  ChevronRight,
  Star,
  Zap,
  Globe,
  Phone,
  Building2,
  PiggyBank,
  X,
  Landmark,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  ROUTE_PATHS,
  Transaction,
  formatCurrency,
  formatDate,
  CATEGORY_LABELS,
} from '@/lib/index';

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.35 } },
};

const CATEGORY_ICONS: Record<string, string> = {
  salary: '💰',
  food: '🍔',
  transport: '🚗',
  entertainment: '🎬',
  shopping: '🛍️',
  utilities: '💡',
  health: '🏥',
  investment: '📈',
  transfer: '↔️',
  fee: '📋',
  other: '📦',
};

const QUICK_ACTIONS = [
  {
    icon: Send,
    labelKey: 'dash_send',
    path: 'DASHBOARD_TRANSFER',
    color: '#D4AF37',
    bg: 'rgba(212,175,55,0.18)',
  },
  {
    icon: Plus,
    labelKey: 'dash_top_up',
    path: 'DASHBOARD_TRANSFER',
    color: '#4ade80',
    bg: 'rgba(74,222,128,0.12)',
  },
  {
    icon: CreditCard,
    labelKey: 'cards_title',
    path: 'DASHBOARD_CARDS',
    color: '#60a5fa',
    bg: 'rgba(96,165,250,0.12)',
  },
  {
    icon: BarChart3,
    labelKey: 'tx_title',
    path: 'DASHBOARD_TRANSACTIONS',
    color: '#c084fc',
    bg: 'rgba(192,132,252,0.12)',
  },
];

function getGreetingKey(): string {
  const h = new Date().getHours();
  if (h < 12) return 'good_morning';
  if (h < 18) return 'good_afternoon';
  return 'good_evening';
}

const SPENDING_CATEGORIES = [
  {
    key: 'shopping',
    labelKey: 'tx_shopping',
    icon: '🛍️',
    color: '#60a5fa',
    max: 800,
  },
  { key: 'food', labelKey: 'tx_food', icon: '🍔', color: '#4ade80', max: 500 },
  {
    key: 'transport',
    labelKey: 'tx_transport',
    icon: '🚗',
    color: '#fb923c',
    max: 300,
  },
  {
    key: 'entertainment',
    labelKey: 'tx_entertainment',
    icon: '🎬',
    color: '#c084fc',
    max: 200,
  },
  {
    key: 'utilities',
    labelKey: 'tx_utilities',
    icon: '💡',
    color: '#f87171',
    max: 400,
  },
];

function TopUpModal({
  onClose,
  accountId,
  userId,
  currentBalance,
  onSuccess,
}: {
  onClose: () => void;
  accountId: string;
  userId: string;
  currentBalance: number;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const PRESETS = [10, 50, 100, 250, 500, 1000];

  const handleTopUp = async () => {
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) {
      toast({
        title: t('admin_error'),
        description: t('transfer_insufficient'),
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    try {
      const newBalance = currentBalance + num;
      const { error: txErr } = await supabase.from('transactions').insert({
        account_id: accountId,
        user_id: userId,
        type: 'credit',
        amount: num,
        currency: 'GBP',
        balance_after: newBalance,
        description: note || t('dash_top_up'),
        reference: `TOPUP-${Date.now()}`,
        category: 'other',
        status: 'completed',
      });
      if (txErr) throw new Error(txErr.message);
      const { error: balErr } = await supabase
        .from('bank_accounts')
        .update({ balance: newBalance, available_balance: newBalance })
        .eq('id', accountId);
      if (balErr) throw new Error(balErr.message);
      toast({
        title: t('admin_success'),
        description: `${formatCurrency(num)} ${t('dash_top_up')}`,
      });
      onSuccess();
      onClose();
    } catch (e) {
      toast({
        title: t('admin_error'),
        description: String(e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-3xl p-6 w-full max-w-md shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
              <Landmark className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="font-bold text-foreground text-base">
                {t('dash_top_up')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('dash_available')}:{' '}
                <span className="text-foreground font-semibold">
                  {formatCurrency(currentBalance)}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="mb-4">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            {t('transfer_amount')}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => setAmount(String(p))}
                className={`py-2 rounded-xl text-sm font-semibold border transition-all ${
                  amount === String(p)
                    ? 'bg-green-500/15 border-green-500/40 text-green-400'
                    : 'border-border/50 text-muted-foreground hover:border-border hover:text-foreground'
                }`}
              >
                £{p}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            {t('transfer_amount')} (GBP)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-lg">
              £
            </span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full h-12 pl-9 pr-4 bg-muted/50 border border-border rounded-xl text-foreground text-lg font-bold focus:outline-none focus:ring-2 focus:ring-green-500/30"
            />
          </div>
        </div>

        <div className="mb-5">
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            {t('admin_note')}
          </label>
          <input
            type="text"
            placeholder={t('admin_note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full h-10 px-4 bg-muted/50 border border-border rounded-xl text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <button
          onClick={handleTopUp}
          disabled={loading || !amount}
          className="w-full h-12 rounded-xl font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
          }}
        >
          {loading
            ? t('admin_submit')
            : `${t('dash_top_up')} ${
                amount ? `£${parseFloat(amount).toFixed(2)}` : ''
              }`}
        </button>
      </motion.div>
    </div>
  );
}

export default function Dashboard() {
  const { profile, account, refreshProfile } = useAuth();
  const { t } = useI18n();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [hideBalance, setHideBalance] = useState(false);
  const [copiedIban, setCopiedIban] = useState(false);
  const [copiedAcc, setCopiedAcc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTopUp, setShowTopUp] = useState(false);

  const fetchTransactions = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    const { data } = await supabase
      .from('transactions')
      .select('*')
      .eq('account_id', account.id)
      .order('created_at', { ascending: false })
      .limit(8);
    setTransactions((data ?? []) as Transaction[]);
    setLoading(false);
  }, [account]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const copy = (text: string, which: 'iban' | 'acc') => {
    navigator.clipboard.writeText(text);
    if (which === 'iban') {
      setCopiedIban(true);
      setTimeout(() => setCopiedIban(false), 2000);
    } else {
      setCopiedAcc(true);
      setTimeout(() => setCopiedAcc(false), 2000);
    }
  };

  const totalIn = transactions
    .filter((tx) => ['credit', 'transfer_in', 'interest'].includes(tx.type))
    .reduce((s, tx) => s + tx.amount, 0);
  const totalOut = transactions
    .filter((tx) => ['debit', 'transfer_out', 'fee'].includes(tx.type))
    .reduce((s, tx) => s + tx.amount, 0);
  const savingsRate =
    totalIn > 0 ? Math.round(((totalIn - totalOut) / totalIn) * 100) : 0;

  const spendingByCategory = SPENDING_CATEGORIES.map((cat) => {
    const spent = transactions
      .filter(
        (tx) =>
          tx.category === cat.key &&
          ['debit', 'transfer_out'].includes(tx.type)
      )
      .reduce((s, tx) => s + tx.amount, 0);
    return { ...cat, spent };
  });

  const fullName = profile
    ? `${profile.first_name} ${profile.last_name}`.trim()
    : '';
  const initials = fullName
    ? fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'CYF';
  const planLabel = profile?.plan ?? 'Standard';
  const isVerified = profile?.kyc_status === 'verified';

  return (
    <DashboardLayout title={t('dash_title')}>
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="space-y-5 max-w-6xl mx-auto"
      >
        {account?.status === 'frozen' && (
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-3 bg-destructive/10 border border-destructive/20 rounded-2xl p-4">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm font-medium text-destructive">
                {t('dash_frozen_warning')}
              </p>
            </div>
          </motion.div>
        )}

        {profile?.kyc_status && profile.kyc_status !== 'verified' && (
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-400">
                  {profile.kyc_status === 'pending' && t('dash_kyc_pending')}
                  {profile.kyc_status === 'submitted' && t('dash_kyc_submitted')}
                  {profile.kyc_status === 'rejected' && t('dash_kyc_rejected')}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400/30 text-amber-400 hover:bg-amber-400/10 shrink-0"
                asChild
              >
                <Link to={ROUTE_PATHS.DASHBOARD_PROFILE}>
                  {t('dash_kyc_action')}
                </Link>
              </Button>
            </div>
          </motion.div>
        )}

        <motion.div variants={scaleIn}>
          <div
            className="relative rounded-3xl overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, #0d1b3e 0%, #0a1628 40%, #141e3a 100%)',
              minHeight: 220,
            }}
          >
            <div
              className="absolute top-0 right-0 w-80 h-80 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)',
                transform: 'translate(30%, -30%)',
              }}
            />
            <div
              className="absolute bottom-0 left-0 w-64 h-64 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(96,165,250,0.08) 0%, transparent 70%)',
                transform: 'translate(-30%, 30%)',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 80% 20%, rgba(212,175,55,0.06) 0%, transparent 50%)',
              }}
            />

            <div className="relative p-6 lg:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold select-none"
                    style={{
                      background:
                        'linear-gradient(135deg, #D4AF37 0%, #b8941f 100%)',
                      color: '#0a1628',
                    }}
                  >
                    {initials}
                  </div>
                  <div>
                    <p className="text-white/60 text-xs mb-0.5">
                      {t(getGreetingKey())},
                    </p>
                    <p className="text-white font-semibold text-base leading-tight">
                      {fullName || t('dash_client')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isVerified && (
                    <span
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                      style={{
                        background: 'rgba(74,222,128,0.15)',
                        color: '#4ade80',
                      }}
                    >
                      <Shield className="w-3 h-3" /> {t('kyc_verified')}
                    </span>
                  )}
                  <span
                    className="text-xs px-2.5 py-1 rounded-full font-medium"
                    style={{
                      background: 'rgba(212,175,55,0.2)',
                      color: '#D4AF37',
                      border: '1px solid rgba(212,175,55,0.3)',
                    }}
                  >
                    <Star className="w-3 h-3 inline mr-1" />
                    {planLabel}
                  </span>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row lg:items-end gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-white/60 text-xs uppercase tracking-widest">
                      {t('dash_available')}
                    </p>
                    <button
                      onClick={() => setHideBalance(!hideBalance)}
                      className="text-white/40 hover:text-white/80 transition-colors"
                    >
                      {hideBalance ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p
                    className="font-bold text-white mb-3"
                    style={{
                      fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                      lineHeight: 1.1,
                    }}
                  >
                    {hideBalance ? (
                      <span className="tracking-widest">••••••</span>
                    ) : (
                      formatCurrency(account?.balance ?? 0)
                    )}
                  </p>

                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/45 text-xs">
                        {t('dash_account_number')}:
                      </span>
                      <span className="text-white/85 text-xs font-mono">
                        {account?.account_number ?? '—'}
                      </span>
                      <button
                        onClick={() =>
                          copy(account?.account_number ?? '', 'acc')
                        }
                        className="text-white/40 hover:text-white/80 transition-colors"
                      >
                        {copiedAcc ? (
                          <Check className="w-3 h-3 text-green-400" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/45 text-xs">
                        {t('dash_sort_code')}:
                      </span>
                      <span className="text-white/85 text-xs font-mono">
                        {account?.sort_code ?? '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/45 text-xs">IBAN:</span>
                      <span className="text-white/85 text-xs font-mono">
                        {account?.iban ?? '—'}
                      </span>
                      <button
                        onClick={() => copy(account?.iban ?? '', 'iban')}
                        className="text-white/40 hover:text-white/80 transition-colors"
                      >
                        {copiedIban ? (
                          <Check className="w-3 h-3 text-green-400" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap lg:flex-nowrap">
                  {QUICK_ACTIONS.map((action) => {
                    const Icon = action.icon;
                    const isTopUp = action.labelKey === 'dash_top_up';
                    const path =
                      ROUTE_PATHS[
                        action.path as keyof typeof ROUTE_PATHS
                      ];
                    if (isTopUp) {
                      return (
                        <motion.div
                          key={action.labelKey}
                          whileHover={{ scale: 1.07 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowTopUp(true)}
                          className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-2xl cursor-pointer min-w-[64px]"
                          style={{
                            background: action.bg,
                            border: `1px solid ${action.color}22`,
                          }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: action.color }}
                          />
                          <span className="text-[10px] font-medium text-white/80 whitespace-nowrap">
                            {t(action.labelKey)}
                          </span>
                        </motion.div>
                      );
                    }
                    return (
                      <Link key={action.labelKey} to={path}>
                        <motion.div
                          whileHover={{ scale: 1.07 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-2xl cursor-pointer min-w-[64px]"
                          style={{
                            background: action.bg,
                            border: `1px solid ${action.color}22`,
                          }}
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: action.color }}
                          />
                          <span className="text-[10px] font-medium text-white/80 whitespace-nowrap">
                            {t(action.labelKey)}
                          </span>
                        </motion.div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              icon: TrendingUp,
              label: t('tx_income'),
              value: formatCurrency(totalIn),
              color: '#4ade80',
              bg: 'rgba(74,222,128,0.08)',
              border: 'rgba(74,222,128,0.2)',
            },
            {
              icon: TrendingDown,
              label: t('tx_expenses'),
              value: formatCurrency(totalOut),
              color: '#f87171',
              bg: 'rgba(248,113,113,0.08)',
              border: 'rgba(248,113,113,0.2)',
            },
            {
              icon: PiggyBank,
              label: t('dash_savings_rate'),
              value: `${savingsRate}%`,
              color: '#D4AF37',
              bg: 'rgba(212,175,55,0.08)',
              border: 'rgba(212,175,55,0.2)',
            },
            {
              icon: Wallet,
              label: t('dash_account_status'),
              value:
                account?.status === 'active'
                  ? t('dash_status_active')
                  : account?.status === 'frozen'
                  ? t('dash_status_frozen')
                  : '—',
              color: '#60a5fa',
              bg: 'rgba(96,165,250,0.08)',
              border: 'rgba(96,165,250,0.2)',
            },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div key={i} variants={fadeUp}>
                <Card className="border-border/40 hover:border-border/70 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{
                          background: stat.bg,
                          border: `1px solid ${stat.border}`,
                        }}
                      >
                        <Icon
                          className="w-4.5 h-4.5"
                          style={{ color: stat.color }}
                        />
                      </div>
                    </div>
                    <p className="text-xl font-bold text-foreground">
                      {stat.value}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {stat.label}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <motion.div variants={fadeUp} className="lg:col-span-2">
            <Card className="border-border/40 h-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3 border-b border-border/30">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-4.5 h-4.5 text-gold" />
                  {t('dash_recent_tx')}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs gap-1 text-muted-foreground hover:text-foreground"
                  asChild
                >
                  <Link to={ROUTE_PATHS.DASHBOARD_TRANSACTIONS}>
                    {t('dash_view_all')}{' '}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 animate-pulse"
                      >
                        <div className="w-10 h-10 bg-muted rounded-xl shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3 bg-muted rounded w-2/3" />
                          <div className="h-2 bg-muted rounded w-1/3" />
                        </div>
                        <div className="h-4 bg-muted rounded w-16" />
                      </div>
                    ))}
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {t('dash_no_tx')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      asChild
                    >
                      <Link to={ROUTE_PATHS.DASHBOARD_TRANSFER}>
                        {t('dash_send')}
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-border/20">
                    {transactions.map((tx) => {
                      const isPos = [
                        'credit',
                        'transfer_in',
                        'interest',
                      ].includes(tx.type);
                      const icon = CATEGORY_ICONS[tx.category ?? 'other'] ?? '📦';
                      return (
                        <motion.div
                          key={tx.id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group"
                        >
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-base shrink-0"
                            style={{
                              background: isPos
                                ? 'rgba(74,222,128,0.1)'
                                : 'rgba(248,113,113,0.1)',
                              border: isPos
                                ? '1px solid rgba(74,222,128,0.2)'
                                : '1px solid rgba(248,113,113,0.2)',
                            }}
                          >
                            <span>{icon}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {tx.description}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-xs text-muted-foreground">
                                {formatDate(tx.created_at)}
                              </span>
                              <span className="text-muted-foreground/40">·</span>
                              <span className="text-xs text-muted-foreground capitalize">
                                {CATEGORY_LABELS[tx.category ?? 'other'] ?? tx.category}
                              </span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className={`text-sm font-bold ${
                                isPos ? 'text-green-400' : 'text-foreground'
                              }`}
                            >
                              {isPos ? '+' : '−'}
                              {formatCurrency(Math.abs(tx.amount))}
                            </p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {tx.status}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <div className="space-y-5">
            <motion.div variants={fadeUp}>
              <Card className="border-border/40">
                <CardHeader className="pb-3 border-b border-border/30">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Zap className="w-4 h-4 text-gold" />
                    {t('tx_expenses')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {spendingByCategory.map((cat) => (
                    <div key={cat.key}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">{cat.icon}</span>
                          <span className="text-xs text-muted-foreground">
                            {t(cat.labelKey)}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-foreground">
                          {formatCurrency(cat.spent)}
                        </span>
                      </div>
                      <Progress
                        value={
                          cat.max > 0
                            ? Math.min(100, (cat.spent / cat.max) * 100)
                            : 0
                        }
                        className="h-1.5"
                        style={
                          {
                            '--progress-color': cat.color,
                          } as React.CSSProperties
                        }
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Card className="border-border/40">
                <CardHeader className="pb-3 border-b border-border/30">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gold" />
                    {t('dash_account_details')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {[
                    {
                      label: t('dash_account_number'),
                      value: account?.account_number ?? '—',
                      mono: true,
                    },
                    {
                      label: t('dash_sort_code'),
                      value: account?.sort_code ?? '—',
                      mono: true,
                    },
                    {
                      label: 'IBAN',
                      value: account?.iban ?? '—',
                      mono: true,
                      truncate: true,
                    },
                    {
                      label: 'BIC',
                      value: account?.bic ?? '—',
                      mono: true,
                    },
                    {
                      label: t('dash_currency'),
                      value: account?.currency ?? 'GBP',
                      mono: false,
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between"
                    >
                      <span className="text-xs text-muted-foreground">
                        {row.label}
                      </span>
                      <span
                        className={`text-xs font-medium text-foreground ${
                          row.mono ? 'font-mono' : ''
                        } ${row.truncate ? 'max-w-[140px] truncate' : ''}`}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={fadeUp}>
              <Card className="border-border/40">
                <CardContent className="p-4 grid grid-cols-3 gap-2">
                  {[
                    {
                      icon: Phone,
                      label: t('transfer_title'),
                      path: ROUTE_PATHS.DASHBOARD_TRANSFER,
                      color: '#D4AF37',
                    },
                    {
                      icon: Globe,
                      label: t('tx_title'),
                      path: ROUTE_PATHS.DASHBOARD_TRANSACTIONS,
                      color: '#60a5fa',
                    },
                    {
                      icon: CreditCard,
                      label: t('cards_title'),
                      path: ROUTE_PATHS.DASHBOARD_CARDS,
                      color: '#c084fc',
                    },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link key={item.label} to={item.path}>
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border/30 hover:border-border/60 transition-all text-center cursor-pointer"
                        >
                          <Icon
                            className="w-5 h-5"
                            style={{ color: item.color }}
                          />
                          <span className="text-[10px] text-muted-foreground leading-tight">
                            {item.label}
                          </span>
                        </motion.div>
                      </Link>
                    );
                  })}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <motion.div variants={fadeUp}>
            <Card className="border-border/40">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {t('security_title')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('security_ssl')}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { label: 'FCA Regulated', ok: true },
                    { label: 'FSCS Protected £85k', ok: true },
                    { label: '256-bit SSL Encryption', ok: true },
                    {
                      label: `KYC: ${profile?.kyc_status ?? 'pending'}`,
                      ok: isVerified,
                    },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          item.ok ? 'bg-green-500/15' : 'bg-amber-500/15'
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            item.ok ? 'bg-green-400' : 'bg-amber-400'
                          }`}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={fadeUp}>
            <Card
              className="border-border/40 relative overflow-hidden"
              style={{
                background:
                  'linear-gradient(135deg, rgba(212,175,55,0.06) 0%, transparent 70%)',
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {t('settings_account_plan')}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl font-bold text-foreground">
                        {planLabel}
                      </span>
                      <Badge
                        variant="outline"
                        className="text-xs border-gold/30 text-gold"
                      >
                        {t('settings_current_plan')}
                      </Badge>
                    </div>
                  </div>
                  <Star className="w-6 h-6 text-gold/60" />
                </div>
                <div className="space-y-1.5 mb-4">
                  {[
                    'FCA Regulated Banking',
                    'Instant UK Transfers',
                    '30+ Currencies',
                    'FSCS Protected',
                  ].map((feat) => (
                    <div key={feat} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
                      <span className="text-xs text-muted-foreground">
                        {feat}
                      </span>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-gold/30 text-gold hover:bg-gold/10 text-xs"
                  asChild
                >
                  <Link to={ROUTE_PATHS.DASHBOARD_SETTINGS}>
                    {t('settings_view_plans')}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>

      {showTopUp && account && (
        <TopUpModal
          onClose={() => setShowTopUp(false)}
          accountId={account.id}
          userId={account.user_id}
          currentBalance={account.balance ?? 0}
          onSuccess={() => {
            fetchTransactions();
            refreshProfile();
          }}
        />
      )}
    </DashboardLayout>
  );
}